import { Spinner, StatusBadge, type StatusBadgeTone } from '@immo/ui-kit';
import type { FleetJobEventKind, FleetJobEventView } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { useLocaleFormat } from '@/i18n/use-locale-format';

/**
 * A tone for every kind (BL-024): `dead-letter` is the one line that needs a
 * person, so it alone reads as danger.
 */
const KIND_TONES: Record<FleetJobEventKind, StatusBadgeTone> = {
  claimed: 'info',
  progress: 'neutral',
  phase: 'neutral',
  done: 'success',
  'dead-letter': 'danger',
  note: 'neutral',
};

type Props = {
  readonly events: ReadonlyArray<FleetJobEventView> | null;
  readonly loading: boolean;
};

/**
 * A job's timeline (OPS-54): what its round narrated, oldest first so the
 * newest line is the last one — the order a thread reads in, which is what
 * this replaces.
 */
export function JobTimeline({ events, loading }: Props) {
  const t = useTranslations('pages.admin.jobs');
  const format = useLocaleFormat();

  if (events === null) {
    return loading ? <Spinner size="sm" /> : null;
  }

  if (events.length === 0) {
    return <p className="text-sm text-text-secondary">{t('timeline-empty')}</p>;
  }

  return (
    <ol className="flex flex-col gap-2" aria-label={t('timeline-heading')}>
      {events.map((event) => {
        const needsHuman = event.kind === 'dead-letter';

        return (
          <li
            key={event.id}
            data-kind={event.kind}
            className={
              needsHuman
                ? 'flex flex-col gap-1 rounded-md border-l-4 border-danger-600 bg-danger-50 px-3 py-2'
                : 'flex flex-col gap-1 px-3 py-2'
            }
          >
            <div className="flex items-center gap-2">
              <StatusBadge label={t(`timeline-kind-${event.kind}`)} tone={KIND_TONES[event.kind]} size="sm" />
              <time dateTime={event.at} className="text-xs text-text-tertiary">
                {format.moment(event.at)} · {format.ago(event.at)}
              </time>
            </div>
            <p className="whitespace-pre-wrap break-words text-sm text-text-primary">{event.text}</p>
          </li>
        );
      })}
    </ol>
  );
}
